import { AfterViewInit, Component, OnInit, Renderer2 } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { fromEvent } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { EventTypes } from 'src/app/core/constants/event-types';
import { FileTypePipe } from 'src/app/core/pipes/file-type.pipe';
import { AppConfigService } from 'src/app/core/services/config.service';
import { ConsoleService } from 'src/app/core/services/console.service';
import Adapter from 'src/app/core/utils/file-adapter';
import { BaseFormModule } from 'src/app/pages/shared/base/modules/base-form.module';
import {
  CustomResult,
  GetMenuByTitleDto,
  UpdateMenuCommand,
} from '../../../../core/api/models';
import { MenusService } from '../../../../core/api/services/menus.service';
import { DialogService } from '../../../../core/services/dialog.service';
import { AppDomService } from '../../../../core/services/dom.service';
import { BaseFormPageComponent } from '../../../shared/base/components/base-form-page.component';
import { CreateMenuCommand } from './../../../../core/api/models/create-menu-command';
import { FormPageConfig } from './../../../../core/models/form-page-config.model';
import { FileManagerComponent } from './upload-file/file-manager/file-manager.component';

@Component({
  selector: 'app-menus-form',
  templateUrl: './menus-form.component.html',
  styles: [``],
  imports: [BaseFormModule],
})
export class MenusFormComponent
  extends BaseFormPageComponent
  implements OnInit, AfterViewInit {
  //#region Properties
  url: string = AppConfigService.config.apiUrl;
  formPageConfig: FormPageConfig<UpdateMenuCommand> = {};
  model: GetMenuByTitleDto = {};

  editorData = '';
  editorConfig;

  //#endregion

  constructor(
    protected currentService: MenusService,
    public dialogService: DialogService,
    private fileType: FileTypePipe,
    private translate: TranslateService,
    private renderer: Renderer2
  ) {
    super();
  }

  ngAfterViewInit(): void {
    this.onListenerOpenFileManager();
  }

  onListenerOpenFileManager() {
    try {
      if (this.formPageConfig.isEditMode)
        setTimeout(() => {
          //create element
          const el = document.querySelector(
            '[data-cke-tooltip-text="Block quote"]'
          );
          const uploadFileElement = this.renderer.createElement('i');
          this.renderer.setProperty(
            uploadFileElement,
            'id',
            'text-editor-upload'
          );
          uploadFileElement.className +=
            'mdi mdi-upload mdi-20px tw-text-gray-900 dark:tw-text-white tw-mt-2 tw-px-[4px] upload-file-hover';
          el?.after(uploadFileElement);

          fromEvent(document.getElementById('text-editor-upload'), 'click')
            .pipe(takeUntil(this.onDestroy$))
            .subscribe((event) => {
              let data = {
                menuId: this.formPageConfig.editData.id,
              };

              const modalResult = this.dialogService.openModal({
                component: FileManagerComponent,
                data: data,
              });

              modalResult
                .afterClosed()
                .pipe(
                  takeUntil(this.onDestroy$),
                  tap(() => {
                    AppDomService.removeModalScrollClassFromHtml();
                  })
                )
                .subscribe((result) => {
                  if (result.file) this.getFile(result.file);
                  this.model = result.model;
                });
            });
        }, 200);
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }

  ngOnInit() {
    try {
      this.url = AppConfigService.config.apiUrl;

      super.ngOnInit();
      this.configEditor();

      // if (this.model.textHelp) this.editorData = this.model.textHelp;
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }

  configEditor() {
    try {
      this.editorConfig = {
        dir: 'rtl',

        language: 'fa',
        inline: true,
        enter: 'p',
        uploader: {
          insertImageAsBase64URI: false,
        },
        defaultMode: 0,

        toolbar: [
          'heading',
          '|',
          'bold',
          'italic',
          'link',
          'bulletedList',
          'numberedList',
          'blockQuote',
          'ShowGallery',
          'image',
        ],
      };
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }

  initForm() {
    this.form = new FormGroup({
      generalMenu: new FormGroup({
        title: new FormControl('', Validators.required),
        claimId: new FormControl(''),
        parentId: new FormControl(''),
        route: new FormControl('', Validators.required),
        order: new FormControl(''),
        class: new FormControl(''),
        icon: new FormControl(''),
      }),
      helpMenu: new FormGroup({
        textHelp: new FormControl(''),
        pdfHelp: new FormControl(''),
        videoHelp: new FormControl(''),
        imageHelp: new FormControl(''),
        fileHelp: new FormControl(''),
      }),
    });
  }

  //#region Api

  //Create
  baseCreateApi(model: CreateMenuCommand) {
    this.unSubs();

    this.loading = true;

    this.currentService
      .menusCreatePost({ body: model })
      .pipe(this.basePipeOperators())
      .subscribe(
        (result: CustomResult) => {
          this.apiNotificationService.notifyMessage({ result });
          if (result.isSucceed) {
            // this.uploadFiles(result.detail);

            this.formPageConfig.isEditMode = false;
            this.onSuccess();
          }
        },
        (error) => ConsoleService.error('error', error),
        () => (this.loading = false)
      );
  }

  //Update
  baseUpdateApi(model: UpdateMenuCommand) {
    this.unSubs();

    this.loading = true;

    model.id = this.formPageConfig.editId;
    this.currentService
      .menusUpdatePut({ body: model })
      .pipe(this.basePipeOperators())
      .subscribe(this.baseObserver);
  }

  //#endregion

  onSubmit() {
    try {
      if (!this.isValidForm(this.form, this.tagName)) return;
      if (this.loading) return;

      // this.model.claimId = this.model.claimId || null;
      this.model.parentId = this.model.parentId || null;

      this.checkCreateOrUpdate();
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }

  onReady(editor) {
    try {
      editor.plugins.get('FileRepository').createUploadAdapter = (loader) => {
        return new Adapter(loader, editor.config);
      };
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }

  getFile(file) {
    try {
      let type = this.fileType.transform(file);
      let fileUrl = this.url + '/' + file;

      if (type == 'image')
        this.editorData +=
          '<img style="height:20%;width:20%;" src=' + fileUrl + '>';
      else if (type == 'video')
        this.editorData += this.translate.instant(
          'pages.infrastructure.menus.menusForm.downloadVideo',
          { fileUrl }
        );
      else if (type == 'pdf')
        this.editorData += this.translate.instant(
          'pages.infrastructure.menus.menusForm.downloadPDF',
          { fileUrl }
        );
      else
        this.editorData += this.translate.instant(
          'pages.infrastructure.menus.menusForm.downloadFile',
          { fileUrl }
        );
    } catch (error) {
      this.loggerService.logError(
        this.tagName,
        error,
        EventTypes.infrastructure
      );
    }
  }
}
